import { useTranslation } from "react-i18next";
import { useUpdateSingleAnnouncementAsReadMutation } from "@/services/api.announcements";
import type { ISingleAnnouncement } from "@/types/announcement.interface";
import {
  announcementIconMap,
  extractAnnouncementPreview,
} from "@/utils/announcement.util";
import { Check, Info, Undo2 } from "lucide-react";
import toast from "react-hot-toast";
import { formatNotificaitonTime } from "@/utils/notifications.util";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/routes/paths";

interface IProps {
  announcement: ISingleAnnouncement;
  getAllAnnouncementRefetch: () => void;
  setOpen: (open: boolean) => void;
}

export const AnnouncementItem = ({
  announcement,
  getAllAnnouncementRefetch,
  setOpen,
}: IProps) => {
  const { t } = useTranslation(["common", "nav", "company"]);
  const navigate = useNavigate();
  const { id, type, message, isRead, icon, triggeredAt } = announcement;
  const Icon = announcementIconMap[icon.name] ?? Info;
  const iconBg = `rgba(${icon.color}, 0.12)`;
  const iconColor = `rgb(${icon.color})`;

  const [
    updateMarkSingleAsRead,
    { isLoading: isSingleAnnouncementsReadLoading },
  ] = useUpdateSingleAnnouncementAsReadMutation();

  const handleToggleRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const toastId = toast.loading("Processing...");
    await updateMarkSingleAsRead(id)
      .unwrap()
      .then((res) => {
        getAllAnnouncementRefetch();
        toast.success(
          res.message ??
            (isRead
              ? t("common:inbox.marked_as_unread")
              : t("common:inbox.marked_as_read")),
          { id: toastId },
        );
      })
      .catch((e) => {
        toast.error(
          e?.data?.message ?? e.message ?? t("company:payrolls.action_failed"),
          { id: toastId },
        );
      });
  };

  return (
    <div
      className={`group relative flex items-start gap-3 px-4 py-3.5 cursor-pointer transition-colors duration-150 ${
        !isRead ? "bg-primary/[0.06] hover:bg-primary/10" : "hover:bg-muted/40"
      }`}
      onClick={() => {
        setOpen(false);
        if (!isRead) updateMarkSingleAsRead(id);
        navigate(PATHS.COMMON_PATHS.ANNOUNCEMENTS);
      }}
    >
      {/* Unread left accent bar */}
      {!isRead && (
        <span className="absolute start-0 top-2 bottom-2 w-[3px] rounded-e-full bg-primary" />
      )}

      {/* Icon */}
      <div
        className="flex shrink-0 h-9 w-9 items-center justify-center rounded-xl mt-0.5 shadow-sm"
        style={{ background: iconBg }}
      >
        <Icon size={15} style={{ color: iconColor }} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        <p
          className={`text-[13px] leading-[1.4] line-clamp-1 pe-6 ${
            !isRead
              ? "font-semibold text-foreground"
              : "font-medium text-foreground/80"
          }`}
        >
          {announcement.subject ?? "-"}
        </p>
        <p className="text-[12px] leading-[1.4] line-clamp-1 text-foreground/60 pe-6">
          {extractAnnouncementPreview(message)}
        </p>
        <div className="flex items-center justify-between gap-3 pt-0.5">
          <span className="text-[11px] font-medium text-muted-foreground/80 truncate">
            {type}
          </span>
          <span className="shrink-0 text-[10px] text-muted-foreground/60 tabular-nums whitespace-nowrap">
            {formatNotificaitonTime(triggeredAt)}
          </span>
        </div>
      </div>

      {/* Hover toggle read/unread button */}
      <button
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={
          isRead
            ? t("common:inbox.mark_as_unread")
            : t("common:inbox.mark_as_read")
        }
        disabled={isSingleAnnouncementsReadLoading}
        onClick={handleToggleRead}
        className="absolute top-3 end-3 flex h-6 w-6 items-center justify-center rounded-md border border-border/60 bg-background shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-150 hover:border-primary/40 hover:bg-primary/5 disabled:opacity-40"
        aria-label={
          isRead
            ? t("common:inbox.mark_as_unread")
            : t("common:inbox.mark_as_read")
        }
      >
        {isRead ? (
          <Undo2 size={11} className="text-muted-foreground" />
        ) : (
          <Check size={11} className="text-primary" />
        )}
      </button>
    </div>
  );
};
